// Simple GS1 parser for codes (e.g., DataMatrix) with Application Identifiers (AIs).
// Supports AIs: (01) GTIN (14 digits) and (17) expiry date (YYMMDD).
// Accepts both bracketed format and streams with FNC1 separators (ASCII 29).

export interface Gs1ParsedData {
  gtin?: string;
  expiryDate?: string; // ISO YYYY-MM-DD
}

const GS = String.fromCharCode(29); // ASCII 29

function normalize(input: string): string {
  // Remove invisible characters except GS, normalize spaces
  return input
    .replace(/\r?\n/g, '')
    // Replace literal "<GS>" marker with ASCII 29 group separator
    .replace(/<GS>/g, GS)
    .trim();
}

function yyMmDdToIso(yyMMdd: string): string | undefined {
  if (!/^\d{6}$/.test(yyMMdd)) return undefined;
  const yy = parseInt(yyMMdd.slice(0, 2), 10);
  const mm = parseInt(yyMMdd.slice(2, 4), 10);
  const dd = parseInt(yyMMdd.slice(4, 6), 10);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return undefined;
  const year = 2000 + yy; // Assume range 2000-2099
  const iso = `${year.toString().padStart(4, '0')}-${mm.toString().padStart(2, '0')}-${dd
    .toString()
    .padStart(2, '0')}`;
  return iso;
}

export function parseGs1Data(raw: string): Gs1ParsedData {
  const text = normalize(raw);
  let gtin: string | undefined;
  let expiryDate: string | undefined;

  // 1) Bracketed format: (01)XXXXXXXXXXXXXX(17)YYMMDD...
  const gtinParen = text.match(/\(01\)\s*([0-9]{14})/);
  if (gtinParen) {
    gtin = gtinParen[1];
  }
  const expParen = text.match(/\(17\)\s*([0-9]{6})/);
  if (expParen) {
    expiryDate = yyMmDdToIso(expParen[1]);
  }

  // 2) Non-bracketed format with GS separators or without: 01{14}17{6}
  // Use non-greedy matches and boundaries (start/GS/end)
  if (!gtin) {
    const gtinAi = text.match(new RegExp(`(?:^|${GS})01([0-9]{14})`));
    if (gtinAi) gtin = gtinAi[1];
  }
  if (!expiryDate) {
    // 17 may appear without GS after a fixed-length field (e.g., after 01)
    const expAi = text.match(new RegExp(`(?:^|${GS})17([0-9]{6})`));
    if (expAi) {
      expiryDate = yyMmDdToIso(expAi[1]);
    } else {
      // Try pattern: (start|GS)01{14}17{6}
      const expAfterGtin = text.match(new RegExp(`(?:^|${GS})01[0-9]{14}17([0-9]{6})`));
      if (expAfterGtin) {
        expiryDate = yyMmDdToIso(expAfterGtin[1]);
      } else {
        // Final fallback: any occurrence of 17{6}
        const any17 = text.match(/17([0-9]{6})/);
        if (any17) {
          expiryDate = yyMmDdToIso(any17[1]);
        }
      }
    }
  }

  return { gtin, expiryDate };
}

export function buildProductId(baseCode: string, expiry?: string): string {
  // Create a consistent ID distinguishing products with the same base but different dates
  return expiry ? `${baseCode}|exp:${expiry}` : baseCode;
}


